import { User } from "../components/users/types";
import { EnvironmentID } from "../types/global";

export type WebTrackerMonitoringSite = {
  envID: EnvironmentID;
  domain: string;
};

export type WebTrackerRiskLevel =
  | "low"
  | "medium"
  | "unknown"
  | "high"
  | "removed";

export function needsResolution(riskLevel: WebTrackerRiskLevel): boolean {
  return ["high", "medium", "unknown"].includes(riskLevel);
}

export function riskDisplayName(riskLevel: WebTrackerRiskLevel): string {
  switch (riskLevel) {
    case "high":
      return "high";
    case "medium":
      return "medium";
    case "low":
      return "low";
    case "removed":
      return "not found";
    case "unknown":
    default:
      return "unknown";
  }
}

export type WebTrackerSupportsBAA = "yes" | "no" | "maybe";

export type LatestWebTrackerReportResponse = {
  report: LatestWebTrackerReport;
  trackers: WebTracker[];
  reportTrackerStats: WebTrackerReportTrackerStats[];
};

export type NamedTrackerInfo = {
  name: string;
  riskLevel: WebTrackerRiskLevel;
  description: string;
  domains: string[];
  collectsPHI: boolean;
  supportsBAA: WebTrackerSupportsBAA;
  category: string;
  destinationKey: string;
  destinationSlug: string;
};

export type GetCatalogedTrackersResponse = {
  trackers: NamedTrackerInfo[];
};

export type WebTracker = {
  name: string;
  riskLevel: WebTrackerRiskLevel;
  effectiveRiskLevel: WebTrackerRiskLevel;
  description: string;
  domains: string[];
  category: string;
  supportsBAA: WebTrackerSupportsBAA;
  destinationKey: string;
  destinationSlug: string;
  resolution: WebTrackerResolution;
  resolutionUser: User;
  baa: WebTrackerBAA;
  isPublishedInBlocker: boolean;
};

export function effectiveRiskLevel(
  webtracker: WebTracker
): WebTrackerRiskLevel {
  return webtracker.effectiveRiskLevel ?? webtracker.riskLevel;
}

export function hasVideoProxy(webtracker: WebTracker): boolean {
  return ["YouTube", "Vimeo"].includes(webtracker.name);
}

export function isMaps(webtracker: WebTracker): boolean {
  return webtracker.category.toLowerCase().includes("maps");
}

export function isUninstalled(webtracker: WebTracker): boolean {
  return webtracker.effectiveRiskLevel === "removed";
}

export type WebTrackerReport = {
  id: string;
  domain: string;
  startedAt: Date;
  finishedAt?: Date;
  numVisitedPages: number;
  numFailedPages: number;
  isComplete: boolean;
  isInternal: boolean;
};

export type WebTrackerReportResponse = {
  count: number;
  reports: WebTrackerReport[];
};

export type LatestWebTrackerReport = {
  id: string;
  domain: string;
  startedAt: Date;
  finishedAt: Date;
  isComplete: boolean;
};

export type WebTrackerPage = {
  pageTitle: string;
  pageURL: string;
  trackerURL: string;
};

export type WebTrackerReportTrackerStats = {
  reportId: string;
  tracker: string;
  approxNumPages: number;
  firstDetectedAt: Date;
  lastDetectedAt: Date | null;
  foundDomains: string[];
  examplePages: WebTrackerPage[];
};

export function firstFoundDomain(stats: WebTrackerReportTrackerStats): string {
  if (stats.foundDomains.length <= 0) {
    return "";
  }
  return stats.foundDomains[0];
}

export type WebTrackerResolutionType =
  | "marked_as_safe"
  | "marked_for_removal"
  | "replace_with_freshpaint"
  | "signed_baa"
  | "tracker_blocked"
  | "tracker_first_party"
  | "unresolved";

export type WebTrackerResolution = {
  id: string;
  userId: string;
  domain: string;
  trackerDomain: string;
  resolutionType: WebTrackerResolutionType;
  note: string;
  updatedAt: Date;
  isFPIntegrationConfirmed: boolean;
};

export type WebTrackerResolutionResponse = {
  webTrackerResolution: WebTrackerResolution;
  user: User;
};

export type BAAUploadLinkResponse = {
  uploadLink: string;
  s3ObjectKey: string;
};

export type WebTrackerBAA = {
  id: string;
  accountId: string;
  envId: string;
  domain: string;
  trackerDomain: string;
  s3ObjectKey: string;
  uploadedByUID: string;
  uploadedAt: Date;
  deletedAt: Date;
};

export type BAAResponse = {
  webTrackerBAA: WebTrackerBAA;
  downloadLink: string;
};

export type WebTrackerListDomainsResponse = {
  domains: string[];
};

export type RunningWebTrackerScan = {
  taskARN: string;
  lastStatus: string | null;
  startedAt: Date | null;
  stoppedAt: Date | null;
  startingURL: string;
  params: string[];
};

export type ListRunningWebTrackerScansResponse = {
  scans: RunningWebTrackerScan[];
};

export type RunCrawlerRequest = {
  startingURL: string;
  emails: string[];
};

export type RunCrawlerResponse = {
  scans: RunningWebTrackerScan[];
};

export type WebTrackerBlockerConfig = {
  enabled: boolean;
  blockScripts: boolean;
  blockIFrames: boolean;
  blockImages: boolean;
  blocklist: string[];
};

export type WebTrackerPublishedBlockerConfigResponse = {
  blockerConfig: WebTrackerBlockerConfig;
};

/**
 * Converts a WebTrackerRiskLevel to an integer for sorting purposes.
 */
export function webTrackerRiskLevelAsInt(
  riskLevel: WebTrackerRiskLevel
): number {
  switch (riskLevel) {
    case "removed":
      return 1;
    case "unknown":
      return 2;
    case "low":
      return 3;
    case "medium":
      return 4;
    case "high":
      return 5;
    default:
      return 0;
  }
}

export enum BlockerStatus {
  UP_TO_DATE = "up_to_date",
  CHANGES_PENDING = "changes_pending",
  DISABLED = "disabled",
}

export type OnboardedWTMDomain = {
  domain: string;
  accountID: string;
  projID: string;
  projName: string;
  envID: string;
  envName: string;
  emails: string[];
};

export type ListOnboardedWTMDomainsResponse = {
  domains: OnboardedWTMDomain[];
};
